import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { SubscriptionTier } from '../types';

interface SubscriptionTierContextValue {
  tier: SubscriptionTier;
  /** False until first fetch for current session completes */
  ready: boolean;
  refetch: () => Promise<void>;
}

const SubscriptionTierContext = createContext<SubscriptionTierContextValue | undefined>(
  undefined
);

export function SubscriptionTierProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('basic');
  const [ready, setReady] = useState(false);

  const refetch = useCallback(async () => {
    if (!user) {
      setTier('basic');
      setReady(true);
      return;
    }

    setReady(false);
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('tier')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.warn('[PetNoter] subscription fetch:', error.message);
      setTier('basic');
    } else if (data?.tier === 'premium' || data?.tier === 'basic') {
      setTier(data.tier);
    } else {
      setTier('basic');
    }
    setReady(true);
  }, [user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return (
    <SubscriptionTierContext.Provider value={{ tier, ready, refetch }}>
      {children}
    </SubscriptionTierContext.Provider>
  );
}

export function useSubscriptionTier(): SubscriptionTierContextValue {
  const ctx = useContext(SubscriptionTierContext);
  if (!ctx) {
    throw new Error('useSubscriptionTier must be used inside SubscriptionTierProvider');
  }
  return ctx;
}
